import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getJourneyData } from '../../api/journeyService';
import { logError } from '../utils/logger';
import { hasLeadContactInfo } from '../utils/journeyLeadFields';
import { hydrateStoreFromJourney } from '../utils/journeyResume';
import { decryptJourneyQueryValue, encryptJourneyQueryValue } from '../utils/journeyEncryption';
import store from '../../store';

/**
 * When user lands on term-insurance with fromPaymentReturn=1 and applicationNumber,
 * hydrate the journey store from getJourneyData so Verification/Summary can render with data.
 * Returns { isResuming, isReady } so the component can show loading until hydration is done.
 */
export function usePaymentReturnResume() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const fromPaymentReturn = searchParams.get('fromPaymentReturn');
  const applicationNumberCipher = searchParams.get('applicationNumber')?.trim() || null;
  const applicationNumber =
    applicationNumberCipher
      ? (decryptJourneyQueryValue(applicationNumberCipher) || applicationNumberCipher)
      : null;
  const persistRehydrated = useSelector((s) => s._persist?.rehydrated);
  const leadFormData = useSelector((s) => s.journey?.leadFormData);
  const quoteResult = useSelector((s) => s.journey?.quoteResult);

  const [isResuming, setIsResuming] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (fromPaymentReturn !== '1' || !applicationNumber) {
      setIsReady(true);
      return;
    }
    if (persistRehydrated === false) {
      return;
    }
    // Prefer redux-session data (redux-persist) before calling the API.
    if (hasLeadContactInfo(leadFormData, quoteResult)) {
      setIsReady(true);
      return;
    }
    let cancelled = false;
    setIsResuming(true);
    getJourneyData(applicationNumber)
      .then((journey) => {
        if (cancelled || !journey) return;
        hydrateStoreFromJourney(
          dispatch,
          journey,
          applicationNumber,
          store.getState,
        );
      })
      .catch((err) => {
        logError('[usePaymentReturnResume] Failed to load journey data', err);
      })
      .finally(() => {
        if (!cancelled) {
          setIsResuming(false);
          setIsReady(true);
        }
      });
    return () => { cancelled = true; };
  }, [fromPaymentReturn, applicationNumber, dispatch, persistRehydrated, leadFormData, quoteResult]);

  // After resume, clean URL but KEEP `applicationNumber`.
  // If we remove it completely, a browser refresh can't resume the correct stage and may fall back to /quote.
  useEffect(() => {
    if (!isReady || fromPaymentReturn !== '1' || !applicationNumber) return;
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const encrypted = encryptJourneyQueryValue(applicationNumber) || applicationNumber;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('applicationNumber', encrypted);
        // Prevent re-running resume logic on every refresh.
        url.searchParams.delete('fromPaymentReturn');
        url.searchParams.delete('status');
        url.searchParams.delete('targetStage');
        url.searchParams.delete('paymentFlowMode');

        const nextSearch = url.searchParams.toString();
        const nextUrl = nextSearch ? `${url.pathname}?${nextSearch}` : url.pathname;
        window.history.replaceState(null, document.title || '', nextUrl);
      } catch {
        // Fallback: keep only applicationNumber
        const p = window.location.pathname || '/term-plan';
        window.history.replaceState(
          null,
          document.title || '',
          `${p}?applicationNumber=${encodeURIComponent(encrypted)}`,
        );
      }
    }
  }, [isReady, fromPaymentReturn, applicationNumber]);

  return { isResuming, isReady };
}
